import React, { useState, useEffect, useMemo } from "react";
import {
  Box, Paper, Typography, TextField, Grid, Switch,
  FormControlLabel, MenuItem, Button, Card, CardContent,
  CircularProgress, Backdrop
} from "@mui/material";
import { Stars, Settings, CardGiftcard, Save } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";

// Redux Actions
import { fetchOfferConfigRequest, saveOfferConfigRequest } from "../../../redux/Offers/OfferSlice";

// Constants & Hooks
import {
  vipCustomerCriteriaType, vipCustomerBenefitType, CalculationType,
  ExpireTimeUnit, OfferKeys, MetalAmount
} from "../../../configs/constants";
import useOfferConfig from "../../../redux/Offers/useOfferConfig";
import { canShowPermission } from "../Permissions/permissionObject";

export default function VipConfig() {
  const dispatch = useDispatch();

  const enabledMetals = useSelector((state) => state.settingData?.data?.data?.metals || []);
  const loading = useSelector((state) => state.offer?.loading);
  const { config } = useOfferConfig(OfferKeys.vipcustomerconfig);

  const [permissions, setPermissions] = useState({});
  useEffect(() => {
    const stored = localStorage.getItem("userPermissions");
    if (stored) {
      try {
        setPermissions(JSON.parse(stored));
      } catch (e) {
        console.error("Failed to parse userPermissions", e);
      }
    }
  }, []);

  const canCreate = canShowPermission(permissions, "vipConfig", "create");
  const canEdit = canShowPermission(permissions, "vipConfig", "edit");
  const canDelete = canShowPermission(permissions, "vipConfig", "delete");

  const [vipData, setVipData] = useState([]);
  const [benefits, setBenefits] = useState([]);
  const [errors, setErrors] = useState({});

  const metalAmountOptions = useMemo(() => Object.values(MetalAmount), []);
  const benefitMetalOptions = useMemo(() =>
    enabledMetals.map(m => ({ label: `${m.metalName} (${m.purity})`, value: `${m.metalName}|${m.purity}` })),
    [enabledMetals]
  );

  const getInitialStructures = () => {
    const buyConfigs = enabledMetals.map(m => ({
      metalName: m.metalName, purity: m.purity, bullion: m._id,
      amountType: MetalAmount.Amount, quantity: 0, installments: 0,
      enabled: false, customMessage: ""
    }));

    return {
      criteria: [
        { criteriaType: vipCustomerCriteriaType.buy, displayName: "Metal Purchase ", note: "VIP via one-time or accumulated purchase metal.", enabled: false, config: JSON.parse(JSON.stringify(buyConfigs)) },
        { criteriaType: vipCustomerCriteriaType.sip, displayName: "SIP Installments", note: "VIP via consistent sip installment payments.", installments: 0, enabled: false, customMessage: "" },
        { criteriaType: vipCustomerCriteriaType.buyandsip, displayName: "Metal Purchase and Sip Installments", note: "VIP via both purchase metal and SIP installment.", enabled: false, config: JSON.parse(JSON.stringify(buyConfigs)) },
      ],
      benefits: [
        { 
          benefitType: vipCustomerBenefitType.metalback, displayName: "Metal-Back Reward", note: "Grant bonus metal percentage on purchases.", enabled: false, 
          config: enabledMetals.map(m => ({
            buyMetalName: m.metalName, purity: m.purity, bullion: m._id,
            benefitMetalName: "", benefitPurity: "", benefitMetalSelection: "", benefitBullion: null, 
            calculation_type: CalculationType.Percentage,
            restricted: false, restrictedAmount: 0, 
            quantity: 0, expireTime: "", expireTimeUnit: ExpireTimeUnit.Day, enabled: false, customMessage: ""
          }))
        }
      ]
    };
  };

  useEffect(() => {
    dispatch(fetchOfferConfigRequest({ name: OfferKeys.vipcustomerconfig }));
  }, [dispatch]);

  useEffect(() => {
    if (!enabledMetals.length) return;
    const { criteria: initialCriteria, benefits: initialBenefits } = getInitialStructures();

    if (config && config.config) {
      const hydratedCriteria = initialCriteria.map(def => {
        const match = config.config.find(s => s.criteriaType === def.criteriaType);
        if (!match) return def;
        const merged = { ...def, ...match };
        if (def.config && match.config) {
          merged.config = def.config.map(mDef => {
            const mMatch = match.config.find(sm => sm.metalName === mDef.metalName && sm.purity === mDef.purity);
            return mMatch ? { ...mDef, ...mMatch } : mDef;
          });
        }
        return merged;
      });

      const hydratedBenefits = initialBenefits.map(def => {
        const match = (config.benefits || []).find(s => s.benefitType === def.benefitType);
        if (!match) return def;
        const merged = { ...def, ...match };
        merged.config = def.config.map(bDef => {
          const bMatch = match.config?.find(bm => bm.buyMetalName === bDef.buyMetalName && bm.purity === bDef.purity);
          if (!bMatch) return bDef;
          return {
            ...bDef, ...bMatch,
            benefitMetalSelection: bMatch.benefitMetalName ? `${bMatch.benefitMetalName}|${bMatch.benefitPurity}` : ""
          };
        });
        return merged;
      });
      setVipData(hydratedCriteria);
      setBenefits(hydratedBenefits);
    } else {
      setVipData(initialCriteria);
      setBenefits(initialBenefits);
    }
  }, [config, enabledMetals]);

  const handleCriteriaChange = (idx, key, val) => {
    const updated = [...vipData];
    updated[idx] = { ...updated[idx], [key]: val };
    setVipData(updated);
  };

  const handleMetalConfigChange = (critIdx, metalIdx, key, val) => {
    const updated = [...vipData];
    const newConfig = [...updated[critIdx].config];
    newConfig[metalIdx] = { ...newConfig[metalIdx], [key]: val };
    updated[critIdx] = { ...updated[critIdx], config: newConfig };
    setVipData(updated);
  };

  const handleBenefitChange = (bIdx, mIdx, key, val) => {
    const updated = [...benefits];
    if (mIdx === null) {
      updated[bIdx] = { ...updated[bIdx], [key]: val };
    } else {
      const newConfig = [...updated[bIdx].config];
      const item = { ...newConfig[mIdx], [key]: val };
      if (key === "benefitMetalSelection") {
        const [name, purity] = val.split("|");
        const source = enabledMetals.find(m => m.metalName === name && String(m.purity) === String(purity));
        item.benefitMetalName = name;
        item.benefitPurity = purity;
        item.benefitBullion = source?._id || null;
      }
      newConfig[mIdx] = item;
      updated[bIdx] = { ...updated[bIdx], config: newConfig };
    }
    setBenefits(updated);
  };

  const validate = () => {
    let newErrors = {};
    let isValid = true;
    setErrors(newErrors);
    return isValid;
  };

  const onSave = () => {
    if (!validate()) return;
    const payload = {
      name: OfferKeys.vipcustomerconfig,
      value: {
        config: vipData,
        benefits: benefits.map(b => ({
          ...b,
          config: b.config.map(({ benefitMetalSelection, ...rest }) => ({ ...rest }))
        }))
      }
    };
    dispatch(saveOfferConfigRequest(payload));
  };

  return (
    <Box sx={{ p: 4, maxWidth: 1200, margin: "auto" }}>
      <Backdrop sx={{ color: '#fff', zIndex: 1301 }} open={!!loading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box display="flex" alignItems="center" mb={4}>
        <Stars sx={{ color: "#FFD700", fontSize: 40, mr: 2 }} />
        <Box>
          <Typography variant="h4" fontWeight={800}>VIP Configuration</Typography>
          <Typography color="text.secondary">Manage VIP qualification and rewards</Typography>
        </Box>
      </Box>

      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Settings color="primary" /> Qualification Criteria
      </Typography>

      {vipData.map((crit, cIdx) => (
        <Card key={crit.criteriaType} sx={{ mb: 3, borderLeft: crit.enabled ? '6px solid #1976d2' : '6px solid #e0e0e0' }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="h6" fontWeight={700}>{crit.displayName}</Typography>
                <Typography variant="body2" color="text.secondary">{crit.note}</Typography>
              </Box>
              <Switch checked={crit.enabled} onChange={(e) => handleCriteriaChange(cIdx, 'enabled', e.target.checked)} disabled={!canEdit} />
            </Box>

            {crit.enabled && (
              <Box sx={{ mt: 2 }}>
                {crit.criteriaType === vipCustomerCriteriaType.sip && (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth type="number" label="Minimum Installments" size="small"
                        value={crit.installments}
                        onChange={(e) => handleCriteriaChange(cIdx, 'installments', e.target.value)}
                        disabled={!canEdit}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth label="Custom Message" size="small" placeholder="Enter message for SIP qualification"
                        value={crit.customMessage}
                        onChange={(e) => handleCriteriaChange(cIdx, 'customMessage', e.target.value)}
                        disabled={!canEdit}
                      />
                    </Grid>
                  </Grid>
                )}

                {crit.config && crit.config.map((m, mIdx) => (
                  <Paper key={mIdx} variant="outlined" sx={{ p: 2, mb: 1, bgcolor: m.enabled ? '#f8faff' : 'inherit' }}>
                    <FormControlLabel
                      control={<Switch size="small" checked={m.enabled} onChange={(e) => handleMetalConfigChange(cIdx, mIdx, 'enabled', e.target.checked)} disabled={!canEdit} />}
                      label={<b>{m.metalName} ({m.purity})</b>}
                    />
                    {m.enabled && (
                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} md={4}>
                          <TextField select fullWidth label="Type" size="small" value={m.amountType} onChange={(e) => handleMetalConfigChange(cIdx, mIdx, 'amountType', e.target.value)} disabled={!canEdit}>
                            {metalAmountOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                          </TextField>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField fullWidth type="number" label="Goal Quantity" size="small" value={m.quantity} onChange={(e) => handleMetalConfigChange(cIdx, mIdx, 'quantity', e.target.value)} disabled={!canEdit} />
                        </Grid>
                        {crit.criteriaType === vipCustomerCriteriaType.buyandsip && (
                          <Grid item xs={12} md={4}>
                            <TextField 
                                fullWidth type="number" label="Installments" size="small" 
                                value={m.installments} 
                                onChange={(e) => handleMetalConfigChange(cIdx, mIdx, 'installments', e.target.value)} 
                                disabled={!canEdit}
                            />
                          </Grid>
                        )}
                        <Grid item xs={12}>
                          <TextField 
                            fullWidth label="Custom Message" size="small" placeholder="Enter custom message for this metal"
                            value={m.customMessage} 
                            onChange={(e) => handleMetalConfigChange(cIdx, mIdx, 'customMessage', e.target.value)} 
                            disabled={!canEdit}
                          />
                        </Grid>
                      </Grid>
                    )}
                  </Paper>
                ))}
              </Box>
            )}
          </CardContent>
        </Card>
      ))}

      <Typography variant="h6" sx={{ mt: 5, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <CardGiftcard color="secondary" /> VIP Benefits
      </Typography>

      {benefits.map((b, bIdx) => (
        <Card key={bIdx} sx={{ mb: 4, borderLeft: b.enabled ? '6px solid #9c27b0' : '6px solid #e0e0e0' }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" mb={2}>
              <Typography variant="h6" fontWeight={700}>{b.displayName}</Typography>
              <Switch checked={b.enabled} color="secondary" onChange={(e) => handleBenefitChange(bIdx, null, 'enabled', e.target.checked)} disabled={!canEdit} />
            </Box>
            
            {b.enabled && b.config.map((m, mIdx) => (
              <Box key={mIdx} sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 2, bgcolor: m.enabled ? '#fff' : '#fafafa' }}>
                <Typography variant="subtitle2" color="primary">Purchasing: {m.buyMetalName} ({m.purity})</Typography>
                <Box sx={{ mt: 1, display: 'flex', gap: 4 }}>
                  <FormControlLabel
                    control={<Switch size="small" checked={m.enabled} onChange={(e) => handleBenefitChange(bIdx, mIdx, 'enabled', e.target.checked)} disabled={!canEdit} />}
                    label="Active"
                  />
                  {m.enabled && (
                    <FormControlLabel
                      control={<Switch size="small" color="warning" checked={m.restricted} onChange={(e) => handleBenefitChange(bIdx, mIdx, 'restricted', e.target.checked)} disabled={!canEdit} />}
                      label="Restrict Amount?"
                    />
                  )}
                </Box>
                {m.enabled && (
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} md={4}>
                      <TextField select fullWidth size="small" label="Reward Metal" value={m.benefitMetalSelection} onChange={(e) => handleBenefitChange(bIdx, mIdx, 'benefitMetalSelection', e.target.value)} disabled={!canEdit}>
                        {benefitMetalOptions.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField fullWidth size="small" type="number" label="Reward %" value={m.quantity} onChange={(e) => handleBenefitChange(bIdx, mIdx, 'quantity', e.target.value)} disabled={!canEdit} />
                    </Grid>
                    {m.restricted && (
                      <Grid item xs={12} md={4}>
                        <TextField fullWidth size="small" type="number" label="Upto Restricted Amount*" value={m.restrictedAmount} onChange={(e) => handleBenefitChange(bIdx, mIdx, 'restrictedAmount', e.target.value)} disabled={!canEdit} />
                      </Grid>
                    )}
                    <Grid item xs={6} md={2}>
                      <TextField fullWidth size="small" type="number" label="Expiry" value={m.expireTime} onChange={(e) => handleBenefitChange(bIdx, mIdx, 'expireTime', e.target.value)} disabled={!canEdit} />
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <TextField select fullWidth size="small" label="Unit" value={m.expireTimeUnit} onChange={(e) => handleBenefitChange(bIdx, mIdx, 'expireTimeUnit', e.target.value)} disabled={!canEdit}>
                        {Object.values(ExpireTimeUnit).map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField 
                        fullWidth label="Reward Message" size="small" placeholder="Enter reward description/message"
                        value={m.customMessage} 
                        onChange={(e) => handleBenefitChange(bIdx, mIdx, 'customMessage', e.target.value)} 
                        disabled={!canEdit}
                      />
                    </Grid>
                  </Grid>
                )}
              </Box>
            ))}
          </CardContent>
        </Card>
      ))}

      {(canCreate || canEdit) && (
      <Box display="flex" justifyContent="flex-end" sx={{ mt: 4 }}>
        <Button 
          variant="contained" 
          size="large" 
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Save />} 
          onClick={onSave} 
          disabled={loading}
          sx={{ borderRadius: 2, px: 4, minWidth: 200 }}
        >
          {loading ? "Saving..." : "Save Configuration"}
        </Button>
      </Box>
      )}
    </Box>
  );
}